import { ButtonHTMLAttributes } from 'react';
import { ChevronLeft, ChevronRight } from '../../../icons';
import { cx } from '../../../utils/cx';

type PageItem = number | 'ellipsis';

export interface PaginationProps {
  /** Total number of pages available */
  pageCount: number;
  /** Zero-based index for the current page */
  pageIndex: number;
  /** Callback fired when the page changes */
  onPageChange?: (pageIndex: number) => void;
  /** How many page numbers to reveal on each side of the current page */
  siblingCount?: number;
  /** Optional label for the navigation landmark */
  ariaLabel?: string;
  className?: string;
}

const DOTS: PageItem = 'ellipsis';

export const Pagination = ({
  pageCount,
  pageIndex,
  onPageChange,
  siblingCount = 1,
  ariaLabel = 'Pagination',
  className
}: PaginationProps) => {
  if (!pageCount || pageCount <= 1) {
    return null;
  }

  const clampedIndex = Math.min(Math.max(pageIndex, 0), pageCount - 1);
  const currentPage = clampedIndex + 1;
  const safeSiblingCount = Math.max(0, Math.floor(siblingCount));
  const pages = getPaginationRange({
    currentPage,
    pageCount,
    siblingCount: safeSiblingCount
  });

  const handlePageChange = (nextPage: number) => {
    if (!onPageChange) {
      return;
    }

    onPageChange(Math.min(Math.max(nextPage - 1, 0), pageCount - 1));
  };

  return (
    <nav
      aria-label={ariaLabel}
      className={cx('flex items-center gap-3 text-sm', className)}
    >
      <PaginationButton
        aria-label="Previous page"
        disabled={currentPage === 1}
        onClick={() => handlePageChange(currentPage - 1)}
      >
        <ChevronLeft className="size-4 text-fg-quaternary" aria-hidden="true" />
      </PaginationButton>

      <ol className="flex items-center gap-1" role="list">
        {pages.map((page, index) => {
          if (page === DOTS) {
            return (
              <li key={`dots-${index}`} className="px-2 text-fg-quaternary">
                &hellip;
              </li>
            );
          }

          return (
            <li key={page}>
              <PaginationButton
                aria-current={page === currentPage ? 'page' : undefined}
                isActive={page === currentPage}
                onClick={() => handlePageChange(page)}
              >
                {page}
              </PaginationButton>
            </li>
          );
        })}
      </ol>

      <PaginationButton
        aria-label="Next page"
        disabled={currentPage === pageCount}
        onClick={() => handlePageChange(currentPage + 1)}
      >
        <ChevronRight
          className="size-4 text-fg-quaternary"
          aria-hidden="true"
        />
      </PaginationButton>
    </nav>
  );
};

interface PaginationButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement> {
  isActive?: boolean;
}

const PaginationButton = ({
  isActive,
  className,
  type = 'button',
  ...props
}: PaginationButtonProps) => {
  return (
    <button
      type={type}
      {...props}
      className={cx(
        'inline-flex h-9 min-w-9 items-center justify-center rounded-md px-3 text-sm font-medium transition focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand',
        isActive
          ? 'bg-primary_hover text-secondary'
          : 'text-quaternary hover:bg-primary_hover',
        props.disabled && 'pointer-events-none text-disabled',
        className
      )}
    />
  );
};

const getPaginationRange = ({
  currentPage,
  pageCount,
  siblingCount
}: {
  currentPage: number;
  pageCount: number;
  siblingCount: number;
}): PageItem[] => {
  const boundaryCount = 3;

  const startPages = Array.from({ length: boundaryCount }, (_, i) => i + 1);
  const endPages = Array.from(
    { length: boundaryCount },
    (_, i) => pageCount - boundaryCount + 1 + i
  );

  const siblings = Array.from(
    { length: siblingCount * 2 + 1 },
    (_, i) => currentPage - siblingCount + i
  );

  const allPages = Array.from(
    new Set([...startPages, ...endPages, ...siblings])
  )
    .filter((p) => p > 0 && p <= pageCount)
    .sort((a, b) => a - b);

  const result: PageItem[] = [];

  for (let i = 0; i < allPages.length; i++) {
    const page = allPages[i];
    if (page === undefined) continue;

    if (i > 0) {
      const prevPage = allPages[i - 1];
      if (prevPage !== undefined && page - prevPage > 1) {
        result.push(DOTS);
      }
    }

    result.push(page);
  }

  return result;
};
